<template>
  <div class="choose-resolution">
    <!-- <h2>
      <ResolvedIcon :item="conflict" />
      {{ conflict.current.name }}
    </h2> -->

    <v-card
      v-if="keysArrayLength > 1"
      variant="tonal">
      Conflict: {{ conflictTracker.currentKeyIndex + 1 }} /
      {{ keysArrayLength }}
    </v-card>

    <div
      v-if="keysArrayLength > 1"
      class="navigation">
      <v-btn
        @click="previousConflict"
        :disabled="previousDisabled">
        < Previous Conflict
      </v-btn>
      <v-btn
        @click="nextConflict"
        :disabled="nextDisabled">
        Next Conflict >
      </v-btn>
    </div>
  </div>
  <div class="container">
    <!-- {{ conflict }} -->
    <div
      style="display: flex; justify-content: stretch; width: 100%; gap: 8px"
      v-if="type === 'templates'">
      <v-btn
        style="flex-grow: 1"
        variant="flat"
        v-for="answer in conflictTemplateAnswers"
        :key="answer"
        :class="{
          'answer-selected':
            conflict.conflicts[currentConflictKey] == answer[0],
        }"
        @click="handleAnswer(currentConflictKey, answer)">
        {{ answer[1] }}
      </v-btn>
    </div>
    <div
      style="display: flex; justify-content: stretch; width: 100%; gap: 8px"
      v-else-if="type === 'assets'">
      <v-btn
        style="flex-grow: 1"
        variant="flat"
        v-for="answer in conflictAssetAnswers"
        :key="answer"
        :class="{}">
        {{ answer[1] }} 123
      </v-btn>
    </div>
    <!-- {{ conflict.conflicts }} -->
    <div>
      <v-card-subtitle class="py-3">Issue Details</v-card-subtitle>
      <v-divider class="pb-3" />
      <h2>{{ humanReadable[currentConflictKey] }}</h2>

      <div class="incoming-vs-current">
        <v-card
          variant="tonal"
          style="width: 100%">
          <div class="incoming">
            <h2>Incoming</h2>
            <div class="scene-data-container">
              <SceneData
                v-if="displaySceneData"
                :svars="conflict.incoming[conflictMap[currentConflictKey]]"
                :realtime_mode="false"
                hide_activate_controls="true"
                hide_pin_controls="true"
                hideicons="true"
                :readOnlyMode="true" />
              <div v-else>Not Scene Data</div>
            </div>
          </div>
        </v-card>

        <v-card
          variant="tonal"
          style="width: 100%">
          <div class="current">
            <h2>Current</h2>
            <div class="scene-data-container">
              <SceneData
                v-if="displaySceneData"
                :svars="conflict.current[conflictMap[currentConflictKey]]"
                :realtime_mode="false"
                hide_activate_controls="true"
                hide_pin_controls="true"
                hideicons="true"
                :readOnlyMode="true" />
              <div v-else>Not Scene Data</div>
            </div>
          </div>
        </v-card>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, onMounted, watch } from 'vue';
import type { ComputedRef } from 'vue';
import type {
  AssetConflictInfo,
  AssetConflictsEnum,
  TemplateConflictInfo,
  TemplateConflictsEnum,
} from './conflictResultDefinition';

import {
  TemplateResolutionEnum,
  AssetResolutionEnum,
} from './conflictResultDefinition';

import ResolvedIcon from './ResolvedIcon.vue';
import SceneData from '../../Shared/SceneData.vue';
const props = defineProps<{
  conflict: AssetConflictInfo | TemplateConflictInfo; // Replace with the correct structure
  type: 'templates' | 'assets';
}>();

watch(
  props.conflict.conflicts,
  () => {
    console.log('conflicts', props.conflict.conflicts);
  },
  { immediate: true }
);

const conflictTracker = reactive<{
  incoming: Record<string, string>;
  current: Record<string, string>;
  currentKeyIndex: number;
}>({
  incoming: {},
  current: {},
  currentKeyIndex: 0,
});

const keysArray: ComputedRef<(AssetConflictsEnum | TemplateConflictsEnum)[]> =
  computed(
    () =>
      Object.keys(props.conflict.conflicts) as (
        | AssetConflictsEnum
        | TemplateConflictsEnum
      )[]
  );
const keysArrayLength = computed(() => keysArray.value.length);
const currentConflictKey = computed(
  () => keysArray.value[conflictTracker.currentKeyIndex]
);

function setNextConflict() {
  const nextIncomingProperty =
    //@ts-ignore
    props.conflict.incoming[conflictMap[currentConflictKey.value]];

  const nextCurrentProperty =
    //@ts-ignore
    props.conflict.current[conflictTracker.currentKeyIndex];

  conflictTracker.incoming = nextIncomingProperty;
  conflictTracker.current = nextCurrentProperty;
}

const nextDisabled = computed(() => {
  return conflictTracker.currentKeyIndex === keysArrayLength.value - 1;
});

const previousDisabled = computed(() => {
  return conflictTracker.currentKeyIndex === 0;
});

const conflictsResolved = computed(() => {
  return Object.values(props.conflict.conflicts).every((value) => !!value);
});

onMounted(() => {
  setNextConflict();
});

function nextConflict() {
  if (conflictTracker.currentKeyIndex < keysArrayLength.value - 1) {
    conflictTracker.currentKeyIndex++;
    setNextConflict();
  }
}

function previousConflict() {
  if (conflictTracker.currentKeyIndex > 0) {
    conflictTracker.currentKeyIndex--;
    setNextConflict();
  }
}

function handleAnswer(
  key: AssetConflictsEnum | TemplateConflictsEnum,
  answerArray: templateAnswerArray | assetAnswerArray
) {
  const answerResponse = answerArray[0];
  console.log('handleAnswer', key, answerResponse);
  if (props.conflict.conflicts[key] === answerResponse) {
    props.conflict.conflicts[key] = null;
    return;
  }
  props.conflict.conflicts[key] = answerResponse;
}

const conflictMap: Record<AssetConflictsEnum | TemplateConflictsEnum, string> =
  {
    SCENE_DATA_MISMATCH: 'scene_data',
    SOURCE_MISMATCH: 'src_scene_data',
    NAME_MISMATCH: 'name',
  };

const displaySceneData = computed(() => {
  return ['SCENE_DATA_MISMATCH', 'SOURCE_MISMATCH'].includes(
    currentConflictKey.value
  );
});

const humanReadable: Record<
  AssetConflictsEnum | TemplateConflictsEnum,
  string
> = {
  SCENE_DATA_MISMATCH: 'Scene Data Mismatch',
  SOURCE_MISMATCH: 'Source Mismatch',
  NAME_MISMATCH: 'Name Mismatch',
};

type HumanReadableAnswer = string;
type templateAnswerArray = [TemplateResolutionEnum, HumanReadableAnswer];
type assetAnswerArray = [AssetResolutionEnum, HumanReadableAnswer];

const conflictTemplateAnswers: templateAnswerArray[] = [
  [TemplateResolutionEnum.KEEP_INCOMING, 'Overwrite'],
  [TemplateResolutionEnum.KEEP_CURRENT, 'Ignore'],
  [TemplateResolutionEnum.CREATE_PRESET, 'Create Preset'],
];

const conflictAssetAnswers: assetAnswerArray[] = [
  [AssetResolutionEnum.KEEP_INCOMING, 'Overwrite'],
  [AssetResolutionEnum.KEEP_CURRENT, 'Ignore '],
];

function handleFinished() {
  console.log('handleFinished');
  console.log('would post', props.conflict);
}
</script>

<style scoped>
.answer-selected {
  background-color: gold;
}
.container {
  /* outline: 1px solid red; */
}
.incoming-vs-current {
  /* outline: 1px solid gold; */
  gap: 10px;
  margin: 5px;
  padding: 5px;
  display: flex;
  flex-direction: row;
  justify-content: stretch;
}

.incoming,
.current {
  /* outline: 1px solid blue; */
  display: flex;
  flex-direction: column;
  border-radius: 5px;
  padding: 10px;
  margin: 10px;
}

.scene-data-container {
  /* outline: 1px solid green; */
}
</style>
